import React from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import { Controller } from "react-hook-form";
import LoadMask from "Utils/LoadMask/LoadMask";
import { useCierreInventarioForm } from "../hooks/useCierreInventarioForm";
import { CierreInventarioTable } from "../../components/CierreInventarioTable";
import { ResumenFisicoPadresPT } from "../../components/ResumenFisicoPadresPT";
import { RelojCorte } from "./RelojCorte";
import { DecisionSwal } from "Utils/Utils/DecisionSwal";

export const CierreForm = ({
    regresar,
    navigateToDetalleCierre,
    navigateToListadoCierres,
    nombreUsuario,
}) => {
    const hook = useCierreInventarioForm({
        navigateToDetalleCierre,
        navigateToListadoCierres,
    });

    const {
        formControl,
        formRegister,
        cierre,
        cambiarFecha,
        filasPacas,
        filasSobrante,
        resumenFisicoPadres,
        periodo,
        fechaMinimaCorte,
        reajustesObsoletos,
        existenciasAlCorte,
        fechaUltimoMovimiento,
        CancelarCierre,
        FormSubmit,
        cargando,
    } = hook;

    const ConfirmarCierre = () => {
        let mensaje = "Una vez guardado, el cierre no se podrá modificar y su ";
        mensaje +=
            "inventario físico será el inventario inicial del siguiente ";
        mensaje += "cierre. ¿Deseas continuar?";

        DecisionSwal(mensaje, "Sí, cerrar", "Cancelar", FormSubmit);
    };

    const ConfirmarCancelacion = () => {
        let mensaje = "Se descartará el cierre en proceso con todo lo que ";
        mensaje += "haya registrado en él. Las existencias de bodega no ";
        mensaje += "cambian. ¿Deseas continuar?";

        DecisionSwal(mensaje, "Sí, cancelar", "Regresar", CancelarCierre);
    };

    // esta pantalla solo trabaja el documento que ya está en proceso; si no
    // hay ninguno, el hook devuelve al listado, que es de donde se abre
    if (!cierre) {
        return <LoadMask loading={cargando} blur />;
    }

    return (
        <LoadMask loading={cargando} blur>
            <div className="formulario-fields-row mb-0">
                <div className="formulario-field">
                    <label className="text-nowrap">Responsable</label>
                    <input
                        type="text"
                        className="form-control disabled-input"
                        value={nombreUsuario}
                        disabled
                    />
                </div>

                <div className="formulario-field horizontal gap-1rem">
                    <div className="formulario-field">
                        <label className="text-nowrap">
                            Fecha y hora de corte{" "}
                            {cambiarFecha ? "(Manual)" : "(Actual)"}
                        </label>
                        {cambiarFecha ? (
                            <Controller
                                name="fecha"
                                control={formControl}
                                render={({ field }) => {
                                    // la fecha mínima ya es el inicio de un día
                                    // completo, así que la hora solo se limita
                                    // hacia adelante, en el día de hoy: el
                                    // corte no puede ser futuro. El datepicker
                                    // exige minTime junto con maxTime, por eso
                                    // se manda el inicio del día
                                    const esHoy =
                                        field.value &&
                                        moment(field.value).isSame(
                                            moment(),
                                            "day",
                                        );

                                    return (
                                        <DatePicker
                                            selected={field.value}
                                            onChange={field.onChange}
                                            showTimeSelect
                                            timeFormat="HH:mm"
                                            showPopperArrow={false}
                                            dateFormat="DD/MM/YYYY HH:mm"
                                            placeholderText="Fecha y hora de corte"
                                            minDate={fechaMinimaCorte}
                                            maxDate={moment()}
                                            minTime={moment().startOf("day")}
                                            maxTime={
                                                esHoy
                                                    ? moment()
                                                    : moment().endOf("day")
                                            }
                                        />
                                    );
                                }}
                            />
                        ) : (
                            <RelojCorte />
                        )}
                    </div>
                    <div className="creacion-cierre__check creacion-cierre__check--fecha">
                        <input
                            type="checkbox"
                            className="checkbox"
                            id="cambiar_fecha"
                            {...formRegister("cambiar_fecha")}
                        />
                        <label
                            htmlFor="cambiar_fecha"
                            className="m-0 pl-0 text-nowrap"
                        >
                            Cambiar fecha y hora
                        </label>
                    </div>
                </div>
            </div>

            {periodo && (
                <span className="d-block my-3 help-text">
                    Este cierre abarca los movimientos desde el{" "}
                    <strong>
                        {moment(periodo.fecha_inicial).format(
                            "DD/MM/YYYY HH:mm",
                        )}
                    </strong>
                    {cambiarFecha ? (
                        <React.Fragment>
                            {" "}
                            hasta el{" "}
                            <strong>
                                {moment(periodo.fecha_final).format(
                                    "DD/MM/YYYY HH:mm",
                                )}
                            </strong>
                            .
                        </React.Fragment>
                    ) : (
                        " hasta el momento en que se guarde."
                    )}
                </span>
            )}

            {existenciasAlCorte === false && (
                <div className="creacion-cierre__aviso creacion-cierre__aviso--fecha-manual">
                    Bodega registró su último movimiento el{" "}
                    {moment(fechaUltimoMovimiento).format("DD/MM/YYYY HH:mm")}.
                    Para no desincronizar las existencias, no se permiten
                    reajustes en cierres con fecha de corte igual o anterior a
                    esa fecha y hora. Las pacas por pallet al corte pueden
                    consultarse desde el inventario físico de cada fila.
                </div>
            )}

            {reajustesObsoletos > 0 && (
                <div className="creacion-cierre__aviso creacion-cierre__aviso--obsoletos">
                    Hay {reajustesObsoletos} reajuste(s) desactualizados porque
                    la existencia de esos pallets cambió después de
                    registrarlos. Revíselos antes de cerrar el inventario.
                </div>
            )}

            <ResumenFisicoPadresPT resumen={resumenFisicoPadres} />

            <div className="creacion-cierre__check mt-4">
                <input
                    type="checkbox"
                    className="checkbox"
                    id="incluir_sin_movimiento"
                    {...formRegister("incluir_sin_movimiento")}
                />
                <label
                    htmlFor="incluir_sin_movimiento"
                    className="m-0 pl-0 text-nowrap"
                >
                    Ver productos sin movimientos ni existencia
                </label>
            </div>

            <CierreInventarioTable
                titulo="Pacas de pallets"
                campo="pacas"
                filas={filasPacas}
                hook={hook}
            />

            <CierreInventarioTable
                titulo="Sobrante"
                campo="sobrante"
                filas={filasSobrante}
                hook={hook}
            />

            <div className="d-flex justify-content-center mt-5 gap-1rem">
                <button className="btn btn-outline-dark" onClick={regresar}>
                    Regresar
                </button>
                <button
                    className="btn btn-danger"
                    onClick={ConfirmarCancelacion}
                >
                    Cancelar cierre
                </button>
                <button
                    className="btn btn-primary"
                    onClick={ConfirmarCierre}
                    disabled={reajustesObsoletos > 0}
                >
                    Cerrar inventario
                </button>
            </div>
        </LoadMask>
    );
};
